import type Lenis from 'lenis';

// Module-level singleton for the active Lenis instance.
//
// Smooth scroll is desktop-only and optional: it is absent on mobile/touch, when
// the user prefers reduced motion, on the /admin and /tg-app routes, and whenever
// the ENABLE_SMOOTH_SCROLL kill switch (in App.tsx) is off. So getLenis() can
// legitimately return null, and every caller MUST fall back to native scrolling in
// that case. This tiny singleton lets plain (non-React) modules such as
// services/scrollLock.ts reach the instance without prop-drilling through React.
let lenisInstance: Lenis | null = null;

export const setLenis = (instance: Lenis | null): void => {
  lenisInstance = instance;
};

export const getLenis = (): Lenis | null => lenisInstance;

// Scroll the page to the very top.
//   immediate = true  -> instant jump (used for the route-change reset)
//   immediate = false -> animated glide (used for the Footer "Back to Top")
//
// When Lenis is running we route through it so the jump/animation stays in sync
// with Lenis's internal target (a raw window.scrollTo would be undone on the next
// Lenis frame). When Lenis is absent we fall back to the native scroll API.
export const scrollToTop = (immediate = false): void => {
  const lenis = lenisInstance;
  if (lenis) {
    lenis.scrollTo(0, { immediate });
    return;
  }
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: immediate ? 'auto' : 'smooth' });
  }
};
